// AutoTunedHeader — the persistent top-of-page banner that sits above every
// pro-level generator (MagicDesk · Super Narrate · Deskeynomics).
//
// It does two jobs:
//   1.  Communicates that the page is silently personalised (no topic shown).
//   2.  Lets the student toggle into Manual mode, where they pick a topic
//       chip from `TopicChipPicker` instead of letting the backend infer one.
//
// Backend contract (`GET /api/student/autotune/snapshot?lesson_id=...&kind=...`):
//   { subject, hardness, question_pattern_label, answer_expectation_label,
//     explanation, age_band, signal_strength, ready }
//
// The actual inferred TOPIC is never returned — that lives in the silent
// `hidden_angle` server-side and only gets injected when `autotuned=true`
// is sent to the generate endpoint.
import React, { useEffect, useState } from 'react';
import { Sparkles, ChevronDown, Wand2, SlidersHorizontal, Zap } from 'lucide-react';
import { featureFill, featureInk } from './featureColors';
import api from '../../studentApi';

export default function AutoTunedHeader({
  lessonId,
  kind,                // 'magicdesk' | 'narrate' | 'deskeynomics'
  tint,
  autotuned,
  onChange,            // (next: boolean) => void
  testIdPrefix = 'autotune',
  hideToggle = false,             // when true, hide the Auto/Manual segmented control
  quickActionLabel,               // e.g. "Generate Now" — shows a CTA pill in the header
  onQuickAction,                  // () => void
  quickActionDisabled = false,
}) {
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/student/autotune/snapshot?lesson_id=${lessonId}&kind=${kind}`)
      .then(d => { if (alive) setSnap(d); })
      .catch(() => alive && setSnap(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [lessonId, kind]);

  const handleToggle = (next) => {
    onChange?.(next);
    // Fire-and-forget signal — used by the backend ranker so we know which
    // students are leaning on Auto-Tuned vs hand-picking.
    api.post('/student/autotune/signal', {
      signal: 'autotune_toggled',
      lesson_id: lessonId,
      kind,
      autotuned_on: next,
    }).catch(() => {});
  };

  const strength = snap?.signal_strength ?? 0;
  const ready    = snap?.ready;

  return (
    <div
      data-testid={`${testIdPrefix}-header`}
      data-autotuned={autotuned ? 'true' : 'false'}
      className="relative overflow-hidden rounded-xl border mb-3"
      style={{
        borderColor: autotuned ? `color-mix(in srgb, ${tint} 55%, transparent)` : 'var(--st-border)',
        background: autotuned
          ? `linear-gradient(135deg, color-mix(in srgb, ${tint} 14%, var(--st-card)) 0%, var(--st-card) 100%)`
          : 'var(--st-card)',
      }}>
      {/* Decorative glow when autotuned is ON */}
      {autotuned && (
        <>
          <div className="pointer-events-none absolute -top-12 -right-12 w-40 h-40 rounded-full blur-2xl opacity-40"
            style={{ background: tint }} />
          <div className="pointer-events-none absolute inset-0 opacity-[0.08] mix-blend-overlay"
            style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)', backgroundSize: '4px 4px' }} />
        </>
      )}

      <div className="relative px-3 py-2.5 flex items-center gap-2.5">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition"
          style={{
            background: autotuned ? featureFill(tint) : `color-mix(in srgb, ${tint} 12%, transparent)`,
            color: autotuned ? '#fff' : featureInk(tint),
          }}>
          {autotuned ? <Wand2 className="w-4 h-4" /> : <SlidersHorizontal className="w-4 h-4" />}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs font-semibold "
              style={{ color: autotuned ? featureInk(tint) : 'var(--st-mute)' }}>
              {autotuned ? 'Auto-Tuned · ON' : 'Manual Mode'}
            </span>
            {autotuned && ready && (
              <span data-testid={`${testIdPrefix}-strength`}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-semibold"
                style={{ background: 'color-mix(in srgb, var(--st-card) 60%, transparent)', color: featureInk(tint) }}>
                <Sparkles className="w-2.5 h-2.5" /> {strength}%
              </span>
            )}
          </div>

          <h3 className="text-xs font-semibold leading-tight truncate"
            style={{ fontFamily: 'var(--st-font-heading)', color: 'var(--st-ink)' }}>
            {autotuned
              ? 'Tuned silently for the way you learn'
              : 'Pick the angle yourself'}
          </h3>

          {/* Expandable signal breakdown — visible only when Auto-Tuned is ON. */}
          {autotuned && snap && (
            <>
              <button
                onClick={() => setExpanded(e => !e)}
                data-testid={`${testIdPrefix}-expand`}
                className="mt-1 inline-flex items-center gap-1 text-xs font-semibold opacity-70 hover:opacity-100 transition-opacity"
                style={{ color: 'var(--st-ink)' }}>
                <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                {expanded ? 'Hide signals' : 'What we noticed'}
              </button>
              {expanded && (
                <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-3 gap-1.5"
                  data-testid={`${testIdPrefix}-signal-grid`}>
                  <SignalChip label="You ask"    value={snap.question_pattern_label} tint={tint} />
                  <SignalChip label="You learn"  value={snap.answer_expectation_label} tint={tint} />
                  <SignalChip label="Difficulty" value={snap.hardness} tint={tint} />
                </div>
              )}
            </>
          )}
        </div>

        {/* Toggle group OR Quick Action CTA — segmented control feel */}
        {!hideToggle && (
          <div
            className="shrink-0 inline-flex p-1 rounded-full border"
            style={{ borderColor: 'var(--st-border)', background: 'var(--st-bg)' }}
            data-testid={`${testIdPrefix}-toggle`}>
            <ToggleBtn
              label="Auto"
              active={autotuned}
              tint={tint}
              onClick={() => handleToggle(true)}
              testId={`${testIdPrefix}-toggle-auto`}
              icon={<Wand2 className="w-3 h-3" />}
            />
            <ToggleBtn
              label="Manual"
              active={!autotuned}
              tint={tint}
              onClick={() => handleToggle(false)}
              testId={`${testIdPrefix}-toggle-manual`}
              icon={<SlidersHorizontal className="w-3 h-3" />}
            />
          </div>
        )}
        {hideToggle && quickActionLabel && (
          <button
            onClick={onQuickAction}
            disabled={quickActionDisabled}
            data-testid={`${testIdPrefix}-quick-action`}
            className="shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold text-white inline-flex items-center gap-1 disabled:opacity-50 transition-transform "
            style={{ background: featureFill(tint), boxShadow: `0 6px 16px -8px ${tint}` }}>
            <Zap className="w-3 h-3" /> {quickActionLabel}
          </button>
        )}
      </div>
    </div>
  );
}

function ToggleBtn({ label, active, tint, onClick, testId, icon }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      aria-pressed={active}
      className="px-2.5 py-1 rounded-full text-xs font-semibold transition inline-flex items-center gap-1"
      style={{
        background: active ? featureFill(tint) : 'transparent',
        color: active ? '#fff' : 'var(--st-ink)',
      }}>
      {icon} {label}
    </button>
  );
}

function SignalChip({ label, value, tint }) {
  return (
    <div className="rounded-lg px-2.5 py-1.5 border text-left"
      style={{ borderColor: 'var(--st-border)', background: 'var(--st-bg)' }}>
      <div className="text-xs font-semibold opacity-60">{label}</div>
      <div className="text-xs font-semibold capitalize" style={{ color: featureInk(tint) }}>{value || '—'}</div>
    </div>
  );
}
